import { useMemo } from 'react';
import { motion } from 'framer-motion';

/**
 * ProjectPulse — "radar" visualization of active conversations as orbiting rings.
 * - Each ring rotates at a speed proportional to recency.
 * - Ring color indicates health (Steel Blue = on track, Ember = newer activity).
 * - Inspired by a mission-control radar.
 */
const ProjectPulse = ({ convos = [], size = 240 }) => {
  const rings = useMemo(() => {
    const latest = convos.slice(0, 5);
    if (latest.length === 0) return [];
    const now = Date.now();
    return latest.map((c, i) => {
      const ts = c.updated_at ? new Date(c.updated_at).getTime() : now;
      const ageHours = Math.max(0.1, (now - ts) / 3600000);
      const recency = Math.min(1, 24 / ageHours); // 1 = brand new, 0 = old
      return {
        id: c.id || i,
        title: (c.title || 'Untitled').slice(0, 40),
        radius: 38 + i * 20,
        duration: 24 + i * 6 - recency * 6, // newer = faster
        color: recency > 0.6 ? '#F26B1F' : i === 0 ? '#5790C9' : '#1E4E8C',
        opacity: 0.35 + (latest.length - i) * 0.08,
        recency,
      };
    });
  }, [convos]);

  if (rings.length === 0) {
    return (
      <div className="h-[240px] flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 rounded-full border border-dashed border-white/10 flex items-center justify-center mb-3">
          <div className="ig-live-dot" />
        </div>
        <p className="text-[12px] text-nexus-text-tertiary">Start a conversation<br />to light up the radar</p>
      </div>
    );
  }

  const center = size / 2;
  const maxRadius = Math.max(...rings.map((r) => r.radius));

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      {/* Concentric gridlines */}
      <svg width={size} height={size} className="absolute inset-0">
        <defs>
          <radialGradient id="pulseCenter" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1E4E8C" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1E4E8C" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={center} cy={center} r={maxRadius + 14} fill="url(#pulseCenter)" />
        {[1, 2, 3].map((m) => (
          <circle key={m} cx={center} cy={center} r={(maxRadius / 3) * m}
                  fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={1} strokeDasharray="2 4" />
        ))}
        <line x1={center - maxRadius - 10} y1={center} x2={center + maxRadius + 10} y2={center}
              stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        <line x1={center} y1={center - maxRadius - 10} x2={center} y2={center + maxRadius + 10}
              stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        {/* Core */}
        <circle cx={center} cy={center} r={4} fill="#F26B1F" />
        <circle cx={center} cy={center} r={8} fill="none" stroke="#F26B1F" strokeOpacity="0.4" strokeWidth="1">
          <animate attributeName="r" from="4" to="16" dur="2s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" from="0.6" to="0" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>

      {/* Orbiting rings with a satellite dot */}
      {rings.map((r, i) => (
        <motion.div
          key={r.id}
          animate={{ rotate: 360 }}
          transition={{ duration: r.duration, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div
            className="rounded-full absolute"
            style={{
              width: r.radius * 2,
              height: r.radius * 2,
              border: `1px solid ${r.color}`,
              opacity: r.opacity,
              left: `calc(50% - ${r.radius}px)`,
              top: `calc(50% - ${r.radius}px)`,
            }}
          />
          <div
            className="absolute"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: r.color,
              boxShadow: `0 0 10px ${r.color}`,
              left: `calc(50% + ${r.radius - 4}px)`,
              top: `calc(50% - 4px)`,
            }}
            title={r.title}
          />
        </motion.div>
      ))}

      {/* Caption */}
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 text-center">
        <div className="ig-mono text-[10px] text-nexus-text-tertiary">{rings.length} active thread{rings.length === 1 ? '' : 's'}</div>
      </div>
    </div>
  );
};

export default ProjectPulse;
